/* eslint-disable no-restricted-syntax */
/* eslint-disable no-console */
/* eslint-disable no-await-in-loop */
import { Dispatch, useMemo } from 'react'

import axios from 'axios'
import { stringify } from 'query-string'
import { useHistory, useLocation } from 'react-router-dom'

import { CATEGORY } from 'src/refactor/helpers/constants'
import { client } from 'src/api'
import { GetFacilities, SearchParams } from 'src/api/types'

import { isEmpty, isEqual } from 'lodash'
import { clearNullish } from 'src/refactor/helpers/objects'
import { IFacility } from 'src/shared/types'
import {
  Action,
  ActionFunctions,
  ActionTypes,
  ItemData,
  SearchData,
  State,
} from './types'
import { defaultState } from './initialState'

export const useActions = (dispatch: Dispatch<Action>, state: State) => {
  const history = useHistory()
  const location = useLocation()

  const actions: ActionFunctions = useMemo(
    () => ({
      search: async (searchData: SearchData) => {
        const {
          search = state.search,
          filter = defaultState.filter,
          shouldReplaceSearch = true,
          isFirstSearch = state.isFirstSearch,
        } = searchData

        if (isEmpty(state.results) || !isEqual(search, state.search))
          filter.distance = 20

        const params = {
          ...search,
          ...filter,
          category: state.category,
        }

        if (shouldReplaceSearch) {
          const urlParams = {
            ...params,
          } as Partial<SearchParams>

          // Deleting params with default value to stop url clutter
          if (!urlParams.name) {
            delete urlParams.name
          }
          if (urlParams.distance === 20) {
            delete urlParams.distance
          }
          if (urlParams.gender === 'anyGender') {
            delete urlParams.gender
          }
          if (!urlParams.newPatients) {
            delete urlParams.newPatients
          }
          if (!urlParams.withVirtualCare) {
            delete urlParams.withVirtualCare
          }
          const newSearch = stringify(urlParams)

          if (location.search !== `?${newSearch}`) {
            history.replace({
              search: newSearch,
            })
          }
        }

        if (
          isEqual(search, state.search) &&
          isEqual(filter, state.filter) &&
          !isEmpty(state.results)
        ) {
          return
        }

        const { CancelToken } = axios
        const cancelTokenSource = CancelToken.source()

        dispatch({
          type: ActionTypes.SEARCH_PROVIDERS,
          payload: {
            search,
            filter,
            cancelTokenSource,
          },
        })

        try {
          const minResults = isFirstSearch ? 25 : 0

          if (state.category === CATEGORY.FACILITY) {
            const searchRadius = [20, 100]
            const start = searchRadius.findIndex(r => r > filter.distance)
            const deduplicateFacilities = (items: IFacility[] | undefined) => {
              const deduplicatedFacilitiesMap: { [key: string]: IFacility } = {}
              ;(items ?? []).forEach((item: IFacility) => {
                const key = `${item.businessAs ?? ''}${item.address1 ?? ''}${
                  item.address2 ?? ''
                }${item.city ?? ''}${item.state ?? ''}${item.zipCode ?? ''}`
                if (!deduplicatedFacilitiesMap[key]) {
                  deduplicatedFacilitiesMap[key] = item
                }
              })

              return Object.values(deduplicatedFacilitiesMap)
            }
            let response = await client.getBatchFacilitiesByPlaceId(
              {
                ...clearNullish(search, true),
                ...clearNullish(filter, true),
              },
              cancelTokenSource.token
            )

            for (let i = start; i < searchRadius.length; i++) {
              if (response.data.count.total <= minResults) {
                const distance = searchRadius[i]
                dispatch({
                  type: ActionTypes.SEARCH_RADIUS_INCREASE,
                  payload: {
                    distance,
                    errorMsg: `We are expanding our search radius to find you a facility...`,
                    previousDistance: filter.distance,
                  },
                })

                filter.distance = distance

                response = await client.getBatchFacilitiesByPlaceId(
                  {
                    ...clearNullish(search, true),
                    ...clearNullish(filter, true),
                  },
                  cancelTokenSource.token
                )
              }
            }

            const pageSize = 0
            const maxSearchDistance = searchRadius[searchRadius.length - 1]

            const responseToMaxSearchDistance =
              await client.getBatchFacilitiesByPlaceId(
                {
                  ...clearNullish(search, true),
                  ...clearNullish(
                    {
                      ...filter,
                      distance: maxSearchDistance,
                      pageSize,
                    },
                    true
                  ),
                },
                cancelTokenSource.token
              )

            const maxTotalCount = deduplicateFacilities(
              responseToMaxSearchDistance.data.data
            ).length

            // DEDUP
            const dedupData = deduplicateFacilities(response.data.data)
            response.data.data = dedupData
            response.data.count.total = dedupData.length

            dispatch({
              type: ActionTypes.SEARCH_PROVIDERS_FULFILLED,
              payload: {
                data: response.data as GetFacilities,
                updatedFilter: filter,
                maxTotalCount,
              },
            })

            return
          }

          const searchRadius = [20, 100]
          const start = searchRadius.findIndex(r => r > filter.distance)

          let response = await client.getPracticesAndProviders(
            clearNullish(search, true),
            clearNullish(filter, true),
            cancelTokenSource.token
          )

          for (let i = start; i < searchRadius.length; i++) {
            if (response.count.total <= minResults) {
              const distance = searchRadius[i]
              dispatch({
                type: ActionTypes.SEARCH_RADIUS_INCREASE,
                payload: {
                  distance,
                  errorMsg: `We are expanding our search radius to find you a doctor...`,
                  previousDistance: filter.distance,
                },
              })

              filter.distance = distance

              response = await client.getPracticesAndProviders(
                clearNullish(search, true),
                clearNullish(filter, true),
                cancelTokenSource.token
              )
            }
          }

          const pageSize = 0
          const maxSearchDistance = searchRadius[searchRadius.length - 1]

          const responseToMaxSearchDistance =
            await client.getPracticesAndProviders(
              clearNullish(search, true),
              clearNullish(
                {
                  ...filter,
                  distance: maxSearchDistance,
                },
                true
              ),
              cancelTokenSource.token,
              pageSize
            )

          const countItems = {
            maxTotalCount: responseToMaxSearchDistance.count.totalCountingItems,
            maxTotalCountPractices:
              responseToMaxSearchDistance.count.requestCountPractice
                ?.totalPractices,
            maxTotalCountProviders:
              responseToMaxSearchDistance.count.requestCountProvider?.total,
          }
          const countProviders = {
            maxTotalCount: responseToMaxSearchDistance.count.total,
            maxTotalCountPractices:
              responseToMaxSearchDistance.count.requestCountPractice
                ?.totalProviders,
            maxTotalCountProviders:
              responseToMaxSearchDistance.count.requestCountProvider?.total,
          }

          dispatch({
            type: ActionTypes.SEARCH_PROVIDERS_FULFILLED,
            payload: {
              data: response,
              updatedFilter: filter,
              countProviders,
              countItems,
            },
          })
        } catch (err) {
          dispatch({
            type: ActionTypes.SEARCH_PROVIDERS_FAILED,
            payload: err,
          })
        }
      },
      cancelRequest: () => {
        state.cancelTokenSource?.cancel()

        dispatch({
          type: ActionTypes.CANCEL_REQUEST,
        })
      },
      toggleListView: () => {
        dispatch({
          type: ActionTypes.TOGGLE_LIST_VIEW,
        })

        return state.listView === 'list' ? 'map' : 'list'
      },
      getProvidersByPractice: async ({ search, filter }) => {
        const existingPractice = state.viewPractitionerList.find(
          el => el.practice === search.practiceId
        )
        if (existingPractice && existingPractice.list) {
          return
        }

        dispatch({
          type: ActionTypes.GET_PRACTITIONERS,
          payload: search.practiceId,
        })

        try {
          const { data } = await client.getProvidersByPracticeId(
            clearNullish(search, true),
            clearNullish(filter, true)
          )

          dispatch({
            type: ActionTypes.GET_PROVIDERS_BY_PRACTICE_ID_SUCCEED,
            payload: data,
          })
        } catch (err) {
          dispatch({
            type: ActionTypes.GET_PROVIDERS_BY_PRACTICE_ID_FAILED,
            payload: err,
          })
        }
      },
      setExpandedPractice: (practiceId: ItemData['id'] | null) => {
        return dispatch({
          type: ActionTypes.SET_EXPANDED_PRACTICE,
          payload: {
            practiceId,
          },
        })
      },
      filter: (filter = {}) => {
        const payload = {
          ...state.filter,
          ...filter,
        } as State['filter']

        dispatch({ type: ActionTypes.ON_REQUEST_FILTER, payload })

        const { providerType } = filter

        const filteredResults = state.unfilteredItems?.filter(
          ({ itemType }) =>
            !providerType || providerType === 'all' || itemType === providerType
        ) as ItemData[]

        dispatch({ type: ActionTypes.ON_FILTER, payload: filteredResults })
      },
    }),
    [
      dispatch,
      history,
      state.category,
      state.search,
      state.viewPractitionerList,
      state.filter,
      state.results,
      state.listView,
      location.search,
      state.unfilteredItems,
    ]
  )

  return actions
}
